import React from 'react';
import { loadGoogleMapsScript } from '../util/googleMaps';
import { resolveMapCenter } from '../util/mapCenter';
import { googleMapsApiKeyENV, googleMapIdENV } from '../ENV';
import '../styles/amenitiesGlobal.css';

const DEFAULT_ZOOM = 12;
export const ZOOM_ENVELOPE = 3;
export const BOUND_OFFSET_DEG = 3;

export function wrapLongitude(lng) {
	if (lng >= -180 && lng <= 180) { return lng; }
	return ((lng + 180) % 360 + 360) % 360 - 180;
}

const mercatorY   = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2)) / (2 * Math.PI);
const mercatorLat = (y)   => (2 * Math.atan(Math.exp(y * 2 * Math.PI)) - Math.PI / 2) * 180 / Math.PI;

export function latitudeBounds(lat, widthPx, heightPx) {
	const aspect = widthPx > 0 && heightPx > 0 ? heightPx / widthPx : 1;
	const halfWorld = (BOUND_OFFSET_DEG / 360) * aspect;
	return {
		north: Math.min(90,  mercatorLat(mercatorY(lat) + halfWorld)),
		south: Math.max(-90, mercatorLat(mercatorY(lat) - halfWorld)),
	};
}

class TrafficResponsiveMap extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			css: props.CSSModules[props.condor_render_name],
			mapsReady: false,
		};

		this.mapContainerRef = React.createRef();
		this.googleMap = null;
		this.yahMarker = null;
		this.trafficLayer = null;
		this.idleTimer = null;

		this.containerId = `trafficMap_${props.component_name}`;

		const { lat, lng } = resolveMapCenter(props);
		this.latitude = lat;
		this.longitude = lng;
	}

	componentDidMount() {
		this._mounted = true;
		
		loadGoogleMapsScript(googleMapsApiKeyENV)
			.then(() => {
				if (this._mounted) {
					this.setState({ mapsReady: true }, () => this.createMap());
				}
			})
			.catch((err) => {
				console.error('TrafficMap: Google Maps failed to load:', err.message);
			});
	}

	componentWillUnmount() {
		this._mounted = false;
		clearTimeout(this.idleTimer);
		if (this.trafficLayer) { this.trafficLayer.setMap(null); this.trafficLayer = null; }
		if (this.yahMarker) { this.yahMarker.map = null; this.yahMarker = null; }
		if (this.googleMap && window.google?.maps?.event) {
			window.google.maps.event.clearInstanceListeners(this.googleMap);
		}
		this.googleMap = null;
	}

	createMap() {
		const container = document.getElementById(this.containerId);
		if (!container || !window.google) { return; }

		const mapZoom = this.props.zoom || DEFAULT_ZOOM;

		const { Map } = window.google.maps;

		const { north, south } = latitudeBounds(this.latitude, container.clientWidth, container.clientHeight);

		const mapOptions = {
			center:           { lat: this.latitude, lng: this.longitude },
			zoom:             mapZoom,
			minZoom:          Math.max(0, mapZoom - ZOOM_ENVELOPE),
			maxZoom:          mapZoom + ZOOM_ENVELOPE,
			restriction:      {
				latLngBounds: {
					north,
					south,
					east:  wrapLongitude(this.longitude + BOUND_OFFSET_DEG),
					west:  wrapLongitude(this.longitude - BOUND_OFFSET_DEG),
				},
				strictBounds: true,
			},
			disableDefaultUI: true,
			keyboardShortcuts: false,
			gestureHandling:  'greedy',
			clickableIcons:   false,
		};

		// The setting is the authority — it ships a real default, so it normally wins and
		// GOOGLE_MAP_ID is only break-glass for a cleared field. That makes a stale env.json
		// invisible, so say so when the two disagree.
		const configuredMapId = this.props.google_map_id || googleMapIdENV;
		if (!configuredMapId) {
			console.warn('TrafficMap: No Map ID configured. Rendering a raster map without Advanced Markers — the You-Are-Here pin is skipped. Set the google_map_id setting or GOOGLE_MAP_ID.');
		} else if (this.props.google_map_id && googleMapIdENV && this.props.google_map_id !== googleMapIdENV) {
			console.warn(`TrafficMap: google_map_id setting (${this.props.google_map_id}) differs from GOOGLE_MAP_ID (${googleMapIdENV}); using the setting. Rotate both or clear GOOGLE_MAP_ID — see condor/docs/components/amenities_map.md.`);
		}
		// Only pass mapId when it is real. Google's DEMO_MAP_ID sentinel does not resolve for
		// this project ("Unable to fetch configuration for mapId DEMO_MAP_ID") and forcing it
		// renders nothing at all, so a missing Map ID costs the markers, not the whole map.
		if (configuredMapId) {
			mapOptions.mapId = configuredMapId;
		}

		this.googleMap = new Map(container, mapOptions);

		const { AdvancedMarkerElement } = window.google.maps.marker || {};
		if (configuredMapId && AdvancedMarkerElement) {
			const yahImgEl = document.createElement('img');
			yahImgEl.src = 'https://ts-condor-assets.s3.amazonaws.com/images/icons/yah-icon.png';
			yahImgEl.className = 'amenGoogleYahPin';
			this.yahMarker = new AdvancedMarkerElement({
				position: { lat: this.latitude, lng: this.longitude },
				map:      this.googleMap,
				title:    'You Are Here',
				content:  yahImgEl,
				zIndex:   10,
			});
		}

		this.trafficLayer = new window.google.maps.TrafficLayer();
		this.trafficLayer.setMap(this.googleMap);

		this.googleMap.addListener('drag', () => this.resetIdleTimer());
		this.googleMap.addListener('zoom_changed', () => this.resetIdleTimer());
		this.resetIdleTimer();
	}

	resetIdleTimer() {
		const { navProps } = this.props;
		const configured = parseInt(navProps && navProps.idleTimeoutSeconds, 10);
		const idleTimeoutSeconds = configured > 0 ? configured : 60;
		clearTimeout(this.idleTimer);
		this.idleTimer = setTimeout(() => this.resetMap(), idleTimeoutSeconds * 1000);
	}

	resetMap() {
		if (this.googleMap) {
			this.googleMap.panTo({ lat: this.latitude, lng: this.longitude });
			this.googleMap.setZoom(this.props.zoom || DEFAULT_ZOOM);
		}
	}

	handleZoomIn() {
		if (this.googleMap) {
			this.googleMap.setZoom(this.googleMap.getZoom() + 1);
			this.resetIdleTimer();
		}
	}

	handleZoomOut() {
		if (this.googleMap) {
			this.googleMap.setZoom(this.googleMap.getZoom() - 1);
			this.resetIdleTimer();
		}
	}

	handleRecenter() {
		this.resetMap();
		this.resetIdleTimer();
	}

	handleMapPan(dx, dy) {
		if (!this.googleMap) { return; }
		const PAN_AMOUNT = 80;
		this.googleMap.panBy(dx * PAN_AMOUNT, dy * PAN_AMOUNT);
		this.resetIdleTimer();
	}

	renderMapControls() {
		const gpsIcon = (
			<svg viewBox="0 0 24 24" fill="currentColor" width="1.1em" height="1.1em" aria-hidden="true">
				<path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
			</svg>
		);
		return (
			<div className="amenGoogleMapControls">
				<div className="amenGoogleZoomGroup">
					<button className="amenGoogleCtrlBtn" onClick={() => this.handleRecenter()} aria-label="Current location">{gpsIcon}</button>
					<div className="amenGoogleZoomStack">
						<button className="amenGoogleZoomBtn" onClick={() => this.handleZoomIn()}  aria-label="Zoom in"><i className="fas fa-plus" aria-hidden="true" /></button>
						<button className="amenGoogleZoomBtn" onClick={() => this.handleZoomOut()} aria-label="Zoom out"><i className="fas fa-minus" aria-hidden="true" /></button>
					</div>
				</div>
				<div className="amenGooglePanDpad" aria-label="Pan map">
					<div className="amenGooglePanGrid">
						<span/>
						<button className="amenGoogleCtrlBtn amenGooglePanBtn" onClick={(e) => { this.handleMapPan(0, -1); e.currentTarget.blur(); }} aria-label="Pan up">
							<svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%"><polygon points="12,6 6,17 18,17"/></svg>
						</button>
						<span/>
						<button className="amenGoogleCtrlBtn amenGooglePanBtn" onClick={(e) => { this.handleMapPan(-1, 0); e.currentTarget.blur(); }} aria-label="Pan left">
							<svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%"><polygon points="6,12 17,6 17,18"/></svg>
						</button>
						<span/>
						<button className="amenGoogleCtrlBtn amenGooglePanBtn" onClick={(e) => { this.handleMapPan(1,  0); e.currentTarget.blur(); }} aria-label="Pan right">
							<svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%"><polygon points="18,12 7,6 7,18"/></svg>
						</button>
						<span/>
						<button className="amenGoogleCtrlBtn amenGooglePanBtn" onClick={(e) => { this.handleMapPan(0,  1); e.currentTarget.blur(); }} aria-label="Pan down">
							<svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%"><polygon points="12,18 6,7 18,7"/></svg>
						</button>
						<span/>
					</div>
				</div>
			</div>
		);
	}

	render() {
		const {
			custom_classes,
			modifier_classes,
			keyNum,
			hide_map_buttons,
		} = this.props;

		const {
			css,
			mapsReady,
		} = this.state;

		// Custom classes
		let customClasses = '';
		if (custom_classes && custom_classes.length > 0) {
			custom_classes.forEach((element) => {
				if ((/^([a-z_]|-[a-z_-])[a-z\d_-]*$/i).test(element)) {
					customClasses += ` ${element}`;
				}
			});
		}

		// Modifier classes
		let modifierClasses = '';
		if (modifier_classes && modifier_classes.length > 0) {
			modifier_classes.forEach((element) => {
				if ((/^([a-z_]|-[a-z_-])[a-z\d_-]*$/i).test(element)) {
					modifierClasses += ` ${element}`;
				}
			});
		}

		return (
			<div
				className={`${modifierClasses} ${customClasses} ${css.trafficMapContainer} amenGoogleMapComponent`}
				key={`img${keyNum}`}
				ref={this.mapContainerRef}
				style={{
					width: `100%`,
					height: `100%`,
					position: 'relative',
					'--amenities-secondary-color': 'var(--traffic-secondary-color, var(--amenities-secondary-color, #FFFFFF))',
					'--amenities-highlight-color': 'var(--traffic-highlight-color, var(--amenities-highlight-color, #DEE3E6))',
					'--amenities-text-color': 'var(--traffic-text-color, var(--amenities-text-color, #5E5E5E))',
					'--amenities-icon-color': 'var(--traffic-icon-color, var(--amenities-icon-color, #5E5E5E))',
				}}
			>
				{!mapsReady && (
					<div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
						Loading map...
					</div>
				)}
				<div
					id={this.containerId}
					className="amenGoogleMapCanvas"
					style={{
						width: `100%`,
						height: `100%`,
					}}
				>
				</div>
				{mapsReady && !hide_map_buttons && this.renderMapControls()}
			</div>
		);
	}
}

export default TrafficResponsiveMap;
